/**
 * Firebase Configuration and Initialization
 * 
 * Initializes Firebase, Auth, and App Check with ReCAPTCHA v3
 */

import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getAuth, type Auth } from 'firebase/auth';
import { initializeAppCheck, ReCaptchaV3Provider, type AppCheck, getToken } from 'firebase/app-check';

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || 'unwritten-memories',
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// ReCAPTCHA site key from environment
const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

// Debug token from environment (for local development)
const DEBUG_TOKEN = import.meta.env.VITE_FIREBASE_APPCHECK_DEBUG_TOKEN;

let app: FirebaseApp;
let auth: Auth;
let appCheck: AppCheck | null = null;

/**
 * Initialize Firebase and App Check
 * Call this once at app startup
 */
export function initializeFirebase(): void {
  console.log('🔥 Initializing Firebase...');
  console.log('   Environment:', import.meta.env.MODE);
  console.log('   Project ID:', firebaseConfig.projectId);
  console.log('   ReCAPTCHA Key:', RECAPTCHA_SITE_KEY ? `${RECAPTCHA_SITE_KEY.substring(0, 10)}...` : 'NOT SET');
  
  // Initialize Firebase
  app = initializeApp(firebaseConfig);
  console.log('✅ Firebase app initialized');
  
  // Initialize Firebase Auth
  auth = getAuth(app);
  console.log('✅ Firebase Auth initialized');
  
  // Set debug token if provided (must be set BEFORE initializing App Check)
  if (DEBUG_TOKEN) {
    // Set the global debug token
    // This can be either 'true' (to generate a token) or a specific token string
    (self as any).FIREBASE_APPCHECK_DEBUG_TOKEN = DEBUG_TOKEN === 'true' ? true : DEBUG_TOKEN;
    console.log('🔧 Firebase App Check debug token configured');
    console.log('   If using "true", check console for the generated debug token');
    console.log('   Register it at: https://console.firebase.google.com/ > App Check > Debug Tokens');
  }
  
  // Initialize App Check with ReCAPTCHA v3
  if (RECAPTCHA_SITE_KEY) {
    try {
      console.log('🔐 Initializing App Check with ReCAPTCHA v3...');
      appCheck = initializeAppCheck(app, {
        provider: new ReCaptchaV3Provider(RECAPTCHA_SITE_KEY),
        isTokenAutoRefreshEnabled: true, // Automatically refresh tokens
      });
      console.log('✅ Firebase App Check initialized with ReCAPTCHA v3');
      console.log('   Auto-refresh: ENABLED');
      console.log('   Provider: ReCAPTCHA v3');
      console.log('   Site Key:', RECAPTCHA_SITE_KEY.substring(0, 15) + '...');
      
      // Test token generation
      console.log('🔍 Testing App Check token generation...');
      getToken(appCheck, false)
        .then((token) => {
          console.log('✅ Successfully generated App Check token');
          console.log('   Token preview:', token.token.substring(0, 20) + '...');
        })
        .catch((error) => {
          console.error('❌ Failed to generate App Check token:', error);
          console.error('   Error code:', error.code);
          console.error('   Error message:', error.message);
          if (error.message.includes('quota')) {
            console.warn('⚠️  ReCAPTCHA quota exceeded. Try again later.');
          } else if (error.message.includes('domain')) {
            console.warn('⚠️  Domain not authorized in Firebase Console > App Check > Apps');
            console.warn('   Current domain:', window.location.hostname);
          } else if (error.message.includes('key')) {
            console.warn('⚠️  Invalid ReCAPTCHA site key');
          }
        });
    } catch (error) {
      console.error('❌ Failed to initialize App Check:', error);
      console.error('   Error details:', error instanceof Error ? error.message : error);
      console.warn('⚠️  App Check is required for production. Requests may be blocked.');
      console.warn('');
      console.warn('📋 Troubleshooting steps:');
      console.warn('   1. Verify VITE_RECAPTCHA_SITE_KEY is correct');
      console.warn('   2. Check Firebase Console > App Check > Apps');
      console.warn('   3. Ensure domain is registered:', window.location.hostname);
      console.warn('   4. Verify using ReCAPTCHA v3 (not v2)');
    }
  } else {
    console.warn('⚠️  VITE_RECAPTCHA_SITE_KEY not set. App Check disabled (development mode).');
    console.warn('⚠️  Production requests will be blocked without App Check.');
    console.warn('');
    console.warn('📋 To enable App Check:');
    console.warn('   1. Get ReCAPTCHA site key from Firebase Console');
    console.warn('   2. Set VITE_RECAPTCHA_SITE_KEY in .env.local');
    console.warn('   3. Rebuild the app');
  }
}

/**
 * Get the Firebase app instance
 */
export function getFirebaseApp(): FirebaseApp {
  if (!app) {
    throw new Error('Firebase not initialized. Call initializeFirebase() first.');
  }
  return app;
}

/**
 * Get the Firebase Auth instance
 */
export function getFirebaseAuth(): Auth {
  if (!auth) {
    throw new Error('Firebase not initialized. Call initializeFirebase() first.');
  }
  return auth;
}

/**
 * Get the App Check instance
 */
export function getAppCheck(): AppCheck | null {
  return appCheck;
}

/**
 * Check if App Check is initialized
 */
export function isAppCheckEnabled(): boolean {
  return appCheck !== null;
}


